import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { SEO_MAP } from "@/lib/seoMap";

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const res = await register(name, email, password);
    setLoading(false);
    if (!res.ok) { setError(res.error); toast.error(res.error); return; }
    nav("/portal", { replace: true });
  };

  return (
    <main className="min-h-[100svh] pt-32 pb-20 grid-bg relative overflow-hidden" data-testid="register-page">
      <SEO {...SEO_MAP.register} />
      <div className="hero-orb w-[500px] h-[500px] -top-40 -right-40 glow-secondary" />
      <div className="relative max-w-[440px] mx-auto px-6">
        <div className="overline mb-4">Create account</div>
        <h1 className="font-serif text-5xl tracking-[-0.02em] leading-tight">
          Join the<br /><span className="italic text-gradient">portal</span>.
        </h1>
        <form onSubmit={onSubmit} className="mt-10 space-y-4">
          <Field label="Full name" value={name} onChange={setName} testId="register-name" />
          <Field label="Email" value={email} onChange={setEmail} type="email" testId="register-email" />
          <Field label="Password (min 6)" value={password} onChange={setPassword} type="password" testId="register-password" />
          {error && <div className="text-[13px] text-red-500" data-testid="register-error">{error}</div>}
          <button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-[color:var(--text)] text-[color:var(--bg)] font-semibold text-[14px] inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="register-submit"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Create account"}
          </button>
        </form>
        <div className="mt-8 text-[13px] text-[color:var(--text-muted)]">
          Have an account? <Link to="/login" className="link-underline text-[color:var(--text)]" data-testid="register-to-login">Sign in</Link>
        </div>

        <div className="mt-12 text-[13px] text-[color:var(--text-muted)] leading-relaxed space-y-3">
          <p>
            Create a LaneX Labs client account to access your project portal — a single, secure workspace
            for tracking active projects, reviewing invoices, raising support tickets, exchanging messages
            with your delivery team and managing your domains, hosting and maintenance requests.
          </p>
          <p>
            Your account is protected with encrypted, HTTP-only session cookies and bcrypt-hashed passwords.
            We only ask for the details we need to give you access — no marketing questionnaires, no
            unnecessary fields. Prefer to start with a scoped project brief? Use our{" "}
            <Link to="/contact" className="underline decoration-dotted">six-step enquiry form</Link>
            {" "}and we'll provision your account when engagement begins.
          </p>
          <p>
            Once inside the portal you'll see a live dashboard of every active engagement — progress
            against milestones, upcoming deliverables, latest previews, and threaded messages with the
            engineers and designers on your project. Invoices are visible with their status (paid, due,
            overdue) and can be settled through our payments partner. Support tickets are triaged with
            SLA-backed response times based on your support plan. Client accounts can invite additional
            teammates with per-role permissions, so procurement, engineering and marketing can all view
            the artefacts relevant to them without cross-talk.
          </p>
          <p>
            We take account security seriously: passwords are hashed with bcrypt and never stored in
            plaintext, session cookies are HttpOnly and SameSite-locked, and every authentication event
            is logged for audit. If you ever suspect your account has been compromised, contact
            security@lanexlabs.com and we'll rotate credentials immediately.
          </p>
        </div>
      </div>
    </main>
  );
}
function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="overline block mb-2">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-12 rounded-xl border border-[color:var(--border-strong)] bg-transparent px-4 text-[14.5px] focus:border-brand-primary transition-colors"
        required
        data-testid={testId}
      />
    </label>
  );
}
